import { useEffect, useRef, useState } from "react";
import { api, getAdminToken, API } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { CheckCircle2, CircleDollarSign, FileCheck2, FileX2, Plus, Store, Upload, XCircle, RotateCw, Download } from "lucide-react";

const COI_OPTIONS = [
  { key: "pending", label: "Pending" },
  { key: "submitted", label: "Submitted" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected/Needs Correction" },
];

const VENDOR_STATUS_LABELS = {
  pending: "Applied · Awaiting Payment",
  reserved_coi_pending: "Reserved — COI Pending",
  registered_paid: "Registered/Paid",
  approved: "Approved",
  canceled: "Canceled",
};

const emptyRecordForm = {
  business_name: "", contact_name: "", email: "", phone: "",
  vendor_type: "small_business", description: "",
  is_food_beverage: false, amount_paid: 50, payment_reference: "",
};

export default function VendorsAdminPanel() {
  const [inv, setInv] = useState(null);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showRecord, setShowRecord] = useState(false);
  const [record, setRecord] = useState(emptyRecordForm);
  const csvRef = useRef(null);

  const loadInv = async () => {
    try { const { data } = await api.get("/admin/vendor-inventory"); setInv(data); } catch { /* silent */ }
  };
  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/vendors");
      setRows(data.items || []);
    } catch { toast.error("Could not load vendors."); }
    finally { setLoading(false); }
  };
  useEffect(() => { loadInv(); load(); }, []);

  const markPaid = async (id) => {
    const ref = window.prompt("RunSignup order/reference (optional):", "");
    if (ref === null) return;
    try {
      await api.post(`/admin/vendors/${id}/mark-paid`, { amount_paid: inv?.previous_vendor_price || 50, payment_reference: ref });
      toast.success("Marked paid. Table reserved and campaign suppressed.");
      await Promise.all([loadInv(), load()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not mark paid.");
    }
  };

  const setCOI = async (id, coi_status) => {
    try {
      await api.post(`/admin/vendors/${id}/coi-status`, { coi_status });
      toast.success(`COI status: ${coi_status}`);
      load();
    } catch { toast.error("Could not update COI."); }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this vendor and free the table? This does NOT delete the record.")) return;
    try {
      await api.post(`/admin/vendors/${id}/cancel`);
      toast.success("Vendor canceled.");
      await Promise.all([loadInv(), load()]);
    } catch { toast.error("Cancel failed."); }
  };

  const submitRecord = async (e) => {
    e.preventDefault();
    if (!record.business_name || !record.contact_name || !record.email) {
      toast.error("Business, contact, email are required.");
      return;
    }
    try {
      await api.post("/admin/vendors/record-paid", record);
      toast.success("Paid vendor recorded — table reserved.");
      setRecord(emptyRecordForm);
      setShowRecord(false);
      await Promise.all([loadInv(), load()]);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not record.");
    }
  };

  const importCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/admin/previous-vendors/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Previous vendors imported: ${data.added} added, ${data.updated} updated, ${data.skipped} skipped.`);
    } catch { toast.error("Import failed."); }
    if (csvRef.current) csvRef.current.value = "";
  };

  const exportCsv = () => {
    const token = getAdminToken();
    fetch(`${API}/admin/vendors/export`, { headers: { "X-Admin-Token": token } })
      .then((r) => r.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = "vendors.csv"; a.click();
        URL.revokeObjectURL(url);
      });
  };

  return (
    <div className="space-y-6">
      {/* Inventory pill */}
      {inv && (
        <div className={`p-4 border-2 flex items-center justify-between gap-4 flex-wrap ${inv.sold_out ? "border-[#EC008C] bg-[#EC008C]/10" : "border-[#00A651]/40 bg-[#00A651]/5"}`}>
          <div className="flex items-center gap-3">
            <Store className={`w-6 h-6 ${inv.sold_out ? "text-[#EC008C]" : "text-[#00A651]"}`} />
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold" style={{ color: inv.sold_out ? "#EC008C" : "#00A651" }}>Vendor Inventory</div>
              <div className="font-display text-xl uppercase text-white mt-0.5" data-testid="admin-vendor-inventory-text">
                {inv.sold_out ? <>SOLD OUT · {inv.occupied}/{inv.max_tables} reserved</> : <><span data-testid="admin-tables-remaining">{inv.remaining}</span> of {inv.max_tables} tables remaining · ${inv.previous_vendor_price}</>}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={() => { loadInv(); load(); }} data-testid="admin-refresh-vendors" className="inline-flex items-center gap-2 border border-white/30 hover:border-white text-white font-bold uppercase tracking-widest text-xs px-3 py-1.5"><RotateCw className="w-3.5 h-3.5" /> Refresh</button>
            <button onClick={() => csvRef.current?.click()} data-testid="admin-import-previous-vendors" className="inline-flex items-center gap-2 border-2 border-[#EC008C] text-[#EC008C] hover:bg-[#EC008C] hover:text-white font-bold uppercase tracking-widest text-xs px-3 py-1.5"><Upload className="w-3.5 h-3.5" /> Import Prev Vendor CSV</button>
            <input ref={csvRef} type="file" accept=".csv" onChange={importCsv} className="hidden" />
            <button onClick={exportCsv} className="inline-flex items-center gap-2 border border-white/30 hover:border-[#00A651] hover:text-[#00A651] text-white font-bold uppercase tracking-widest text-xs px-3 py-1.5"><Download className="w-3.5 h-3.5" /> Export</button>
            <button onClick={() => setShowRecord((v) => !v)} data-testid="admin-record-paid-vendor" disabled={inv.sold_out} className="inline-flex items-center gap-2 bg-[#00A651] hover:bg-[#1FBF3E] disabled:bg-[#27272A] disabled:cursor-not-allowed text-black font-bold uppercase tracking-widest text-xs px-3 py-1.5"><Plus className="w-3.5 h-3.5" /> Record Paid Vendor</button>
          </div>
        </div>
      )}

      {showRecord && (
        <form onSubmit={submitRecord} className="bg-[#141414] border border-[#00A651]/40 p-5 grid md:grid-cols-3 gap-3" data-testid="record-paid-vendor-form">
          <Input placeholder="Business name *" value={record.business_name} onChange={(e) => setRecord({ ...record, business_name: e.target.value })} className="bg-black border-[#27272A]" data-testid="record-business" />
          <Input placeholder="Contact name *" value={record.contact_name} onChange={(e) => setRecord({ ...record, contact_name: e.target.value })} className="bg-black border-[#27272A]" data-testid="record-contact" />
          <Input placeholder="Email *" type="email" value={record.email} onChange={(e) => setRecord({ ...record, email: e.target.value })} className="bg-black border-[#27272A]" data-testid="record-email" />
          <Input placeholder="Phone" value={record.phone} onChange={(e) => setRecord({ ...record, phone: e.target.value })} className="bg-black border-[#27272A]" />
          <select value={record.vendor_type} onChange={(e) => setRecord({ ...record, vendor_type: e.target.value })} className="bg-black border border-[#27272A] px-3 py-2 text-sm text-white rounded-md">
            <option value="small_business">Small Business</option>
            <option value="food">Food Vendor</option>
            <option value="resource_partner">Resource Partner</option>
            <option value="community_org">Community Organization</option>
          </select>
          <Input type="number" step="0.01" placeholder="Amount paid" value={record.amount_paid} onChange={(e) => setRecord({ ...record, amount_paid: parseFloat(e.target.value) })} className="bg-black border-[#27272A]" />
          <Input placeholder="RunSignup order # (optional)" value={record.payment_reference} onChange={(e) => setRecord({ ...record, payment_reference: e.target.value })} className="bg-black border-[#27272A] md:col-span-2" />
          <label className="flex items-center gap-3 md:col-span-3">
            <input type="checkbox" checked={record.is_food_beverage} onChange={(e) => setRecord({ ...record, is_food_beverage: e.target.checked })} className="accent-[#EC008C]" data-testid="record-fb" />
            <span className="text-sm text-white">Selling/serving/sampling food or beverage (COI required)</span>
          </label>
          <button type="submit" data-testid="record-submit" className="md:col-span-3 bg-[#00A651] hover:bg-[#1FBF3E] text-black font-bold uppercase tracking-widest text-xs py-2.5">Reserve Table &amp; Suppress Campaign</button>
        </form>
      )}

      {/* Vendor table */}
      <div className="overflow-x-auto bg-[#141414] border border-[#27272A]">
        {loading ? (
          <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-[#71717A] uppercase tracking-widest text-xs">No vendors yet</div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-black text-[#A3A3A3] uppercase tracking-widest text-[10px]">
                <th className="text-left px-3 py-3">When</th>
                <th className="text-left px-3 py-3">Business</th>
                <th className="text-left px-3 py-3">Contact</th>
                <th className="text-left px-3 py-3">Email</th>
                <th className="text-left px-3 py-3">Type</th>
                <th className="text-left px-3 py-3">F&amp;B</th>
                <th className="text-left px-3 py-3">Pay</th>
                <th className="text-left px-3 py-3">Amount</th>
                <th className="text-left px-3 py-3">Vendor Status</th>
                <th className="text-left px-3 py-3">COI</th>
                <th className="text-right px-3 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`vendor-row-${r.id}`} className="border-t border-[#27272A] hover:bg-[#1A1A1A]">
                  <td className="px-3 py-3 text-[#A3A3A3] text-xs">{(r.created_at || "").slice(0, 16).replace("T", " ")}</td>
                  <td className="px-3 py-3 font-bold">{r.business_name}</td>
                  <td className="px-3 py-3">{r.contact_name}</td>
                  <td className="px-3 py-3 text-[#E5E5E5]">{r.email}</td>
                  <td className="px-3 py-3 text-[10px] uppercase tracking-widest text-[#A3A3A3]">{r.vendor_type || "—"}</td>
                  <td className="px-3 py-3">
                    {r.is_food_beverage ? <span className="text-[10px] uppercase text-[#EC008C] font-bold" data-testid={`fb-yes-${r.id}`}>YES</span> : <span className="text-[10px] uppercase text-[#71717A]">No</span>}
                  </td>
                  <td className="px-3 py-3">
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 ${r.payment_status === "paid" ? "bg-[#00A651]/20 text-[#00A651]" : r.payment_status === "canceled" ? "bg-red-500/20 text-red-400" : "bg-[#27272A] text-[#A3A3A3]"}`}>{r.payment_status || "unpaid"}</span>
                  </td>
                  <td className="px-3 py-3 text-[#E5E5E5]">${Number(r.amount_paid || 0).toFixed(2)}</td>
                  <td className="px-3 py-3 text-[10px] uppercase tracking-widest">{VENDOR_STATUS_LABELS[r.vendor_status] || r.vendor_status || "—"}</td>
                  <td className="px-3 py-3">
                    {r.is_food_beverage ? (
                      <select value={r.coi_status || "pending"} onChange={(e) => setCOI(r.id, e.target.value)} data-testid={`coi-select-${r.id}`} className="bg-black border border-[#27272A] text-[10px] uppercase tracking-widest px-2 py-1 rounded text-white">
                        {COI_OPTIONS.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
                      </select>
                    ) : <span className="text-[10px] uppercase text-[#71717A]">Not required</span>}
                  </td>
                  <td className="px-3 py-3 text-right whitespace-nowrap">
                    {r.payment_status !== "paid" && (
                      <button onClick={() => markPaid(r.id)} data-testid={`mark-paid-${r.id}`} title="Mark Paid (reserve table + stop campaign)" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#00A651] hover:text-white px-2 py-1"><CircleDollarSign className="w-3.5 h-3.5" /> Mark Paid</button>
                    )}
                    {r.is_food_beverage && r.coi_status !== "approved" && (
                      <button onClick={() => setCOI(r.id, "approved")} title="Approve COI" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#00A651] hover:text-white px-2 py-1"><FileCheck2 className="w-3.5 h-3.5" /> Approve COI</button>
                    )}
                    {r.is_food_beverage && r.coi_status !== "rejected" && (
                      <button onClick={() => setCOI(r.id, "rejected")} title="Reject COI" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold text-[#EC008C] hover:text-white px-2 py-1"><FileX2 className="w-3.5 h-3.5" /> Reject</button>
                    )}
                    {r.vendor_status !== "canceled" && (
                      <button onClick={() => cancel(r.id)} title="Cancel (frees table)" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#71717A] hover:text-red-400 px-2 py-1"><XCircle className="w-3.5 h-3.5" /></button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-[#71717A]">
        <CheckCircle2 className="w-3 h-3 inline mr-1 text-[#00A651]" /> Marking a vendor paid reserves a table and automatically suppresses future previous-vendor campaign emails to that contact.
      </div>
    </div>
  );
}
